import React, { useEffect, useState } from 'react';
import { Sparkles, X, Loader2, ArrowRight } from 'lucide-react';
import { paymentsApi } from '../services/api';

/**
 * Soft win-back banner shown on the Dashboard when a user previously had
 * Pro (legacy one-shot) and is in the loyalty window — i.e., either
 * approaching expiry (≤7d) or recently expired (≤30d).
 *
 * Server-authoritative: we ALWAYS ask `/api/payments/loyalty-eligibility`
 * before rendering. If the server says `eligible: false`, the banner stays
 * hidden — no client-side guessing of windows.
 *
 * Dismissible per browser-session (localStorage). The user can also opt to
 * pay full price via the Pricing page link.
 */
const DISMISS_KEY = 'softResubscribeDismissedFor';

export const SoftResubscribeBanner = () => {
  const [elig, setElig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await paymentsApi.loyaltyEligibility();
        if (cancelled) return;
        setElig(result);
        // Dismissal is keyed on the user's `expiresAt` — a fresh expiry
        // (after a renewal) re-shows the banner.
        const dismissedFor = window.localStorage.getItem(DISMISS_KEY);
        if (result?.eligible && dismissedFor === result.expiresAt) {
          setDismissed(true);
        }
      } catch (err) {
        // Unauthenticated or server error — silently hide.
        console.debug('[soft-resubscribe] elig fetch:', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!elig?.eligible || dismissed) return null;

  const expiresAt = new Date(elig.expiresAt);
  const days = elig.daysToExpiry;
  const headline = days >= 0
    ? `Your Pro expires in ${days} day${days === 1 ? '' : 's'}`
    : `Your Pro expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`;
  const subline = days >= 0
    ? `Renew before ${expiresAt.toLocaleDateString()} and keep your streak.`
    : 'Pick up where you left off — your skill radar and certificates are still here.';

  const handleClaim = async () => {
    setLoading(true);
    try {
      const { url } = await paymentsApi.createCheckout('pro_monthly_loyalty');
      if (url) {
        window.location.href = url;
        return; // Stripe will own the page from here
      }
    } catch (err) {
      console.error('[soft-resubscribe] checkout failed:', err);
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, elig.expiresAt); } catch (e) { /* noop */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="soft-resubscribe-banner"
      className="relative mb-6 rounded-xl border border-[#00FF00]/30 bg-gradient-to-r from-[#00FF00]/10 via-[#00FF00]/5 to-transparent p-5"
    >
      <button
        type="button"
        onClick={handleDismiss}
        data-testid="soft-resubscribe-dismiss"
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-gray-500 hover:text-white"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-4">
        <div className="hidden sm:flex w-10 h-10 shrink-0 rounded-lg bg-[#00FF00]/15 items-center justify-center">
          <Sparkles className="w-5 h-5 text-[#00FF00]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-semibold text-base">{headline}</h3>
            <span
              data-testid="soft-resubscribe-loyalty-badge"
              className="px-2 py-0.5 text-xs font-mono uppercase tracking-wider rounded-md bg-[#00FF00] text-black"
            >
              First month ${elig.discountedAmount}
            </span>
          </div>
          <p className="text-sm text-gray-300 mt-1.5">
            {subline}{' '}
            <span className="text-gray-500">
              (Normally ${elig.regularAmount}. One-time loyalty offer.)
            </span>
          </p>
          <div className="mt-4">
            <button
              type="button"
              onClick={handleClaim}
              disabled={loading}
              data-testid="soft-resubscribe-claim-btn"
              className="inline-flex items-center gap-2 bg-[#00FF00] hover:bg-[#00DD00] text-black font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors disabled:opacity-60"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Claim ${elig.discountedAmount} offer
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
